import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'scoutly.supportMode';

export type SupportModeState = {
  active: boolean;
  organizationId: string | null;
  organizationName: string | null;
  targetUserId: string | null;
  actorEmail: string | null;
  actorRole: string | null;
  message: string | null;
};

type SupportModeContextValue = {
  supportMode: SupportModeState;
  activateSupportMode: (payload: Omit<SupportModeState, 'active'>) => void;
  clearSupportMode: () => void;
};

const emptyState: SupportModeState = {
  active: false,
  organizationId: null,
  organizationName: null,
  targetUserId: null,
  actorEmail: null,
  actorRole: null,
  message: null,
};

const SupportModeContext = createContext<SupportModeContextValue | null>(null);

function readStoredState(): SupportModeState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState;
    const parsed = JSON.parse(raw) as SupportModeState;
    if (!parsed.active) return emptyState;
    return parsed;
  } catch {
    return emptyState;
  }
}

function persistState(state: SupportModeState) {
  if (!state.active) {
    sessionStorage.removeItem(STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function SupportModeProvider({ children }: { children: React.ReactNode }) {
  const [supportMode, setSupportMode] = useState<SupportModeState>(() => readStoredState());

  useEffect(() => {
    persistState(supportMode);
  }, [supportMode]);

  const activateSupportMode = useCallback((payload: Omit<SupportModeState, 'active'>) => {
    setSupportMode({ ...payload, active: true });
  }, []);

  const clearSupportMode = useCallback(() => {
    setSupportMode(emptyState);
  }, []);

  const value = useMemo(
    () => ({ supportMode, activateSupportMode, clearSupportMode }),
    [supportMode, activateSupportMode, clearSupportMode],
  );

  return <SupportModeContext.Provider value={value}>{children}</SupportModeContext.Provider>;
}

export function useSupportMode(): SupportModeContextValue {
  const ctx = useContext(SupportModeContext);
  if (!ctx) {
    throw new Error('useSupportMode must be used within SupportModeProvider');
  }
  return ctx;
}
